// Raise a loan on a driver's behalf and send it to him to sign.
//
// Everything the driver will read is written by Postgres. Preview shows the exact
// page (driver_loan_preview_v1, drawn by the driver-loans edge function), and it
// refuses in the same sentences Send would, so a manager learns what is missing
// before anything is saved. Send saves the request first and then sends it; if
// SignNow refuses, the loan is kept as "Not sent" with the reason and can be sent
// again, rather than vanishing and being raised twice.
//
// THE VEHICLE IS A CHOICE THAT MUST BE MADE. "No vehicle involved" is an option
// in the same list as the plates, not a missing answer, because the absence of a
// vehicle on a page a driver signs has to have been said by somebody.
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Eye, HandCoins, Send } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../lib/shadcn/dialog'
import { Input } from '../../lib/shadcn/input'
import { Textarea } from '../../lib/shadcn/textarea'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { cn } from '../../lib/shadcn/utils'
import { useCreateLoan, useGetLoanPreview } from '../../hooks/backend/loans'
import { localPhone, pdfBlobUrl, type Options } from '../../utils/loans'

const NO_VEHICLE = '__none__'

type Form = {
  driver_id: string
  category: string
  amount_zar: string
  phone_number: string
  reason: string
  vehicle: string
  event_date: string
  instalments: string
  terms_reason: string
}

const EMPTY: Form = {
  driver_id: '', category: 'airtime', amount_zar: '', phone_number: '', reason: '',
  vehicle: '', event_date: '', instalments: '1', terms_reason: '',
}

type Sent = { ref: string; sent: boolean; driver_email?: string | null; send_error?: string }

function Field({ label, hint, children, htmlFor }: { label: string; hint?: ReactNode; children: ReactNode; htmlFor: string }) {
  return (
    <div className="space-y-1">
      <label htmlFor={htmlFor} className="text-xs font-medium text-foreground">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  )
}

export function RaiseLoanDialog({
  open, onOpenChange, options, presetDriverId, onSaved,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  options: Options | null
  presetDriverId?: string | null
  onSaved: () => void
}) {
  const [form, setForm] = useState<Form>(EMPTY)
  const [error, setError] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [sent, setSent] = useState<Sent | null>(null)
  const preview = useGetLoanPreview<{ pdf_base64: string; signature_page: number }>()
  const create = useCreateLoan<Sent & { ok: boolean }>()

  useEffect(() => {
    if (!open) return
    const d = presetDriverId ? options?.drivers.find((x) => x.id === presetDriverId) : null
    setForm({ ...EMPTY, driver_id: presetDriverId ?? '', phone_number: localPhone(d?.phone) })
    setError(null)
    setSent(null)
    setPreviewUrl(null)
  // Deliberately not keyed on options: a refresh of the list behind an open
  // dialog must never wipe a form the manager is halfway through.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, presetDriverId])

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl) }, [previewUrl])

  const category = options?.categories.find((c) => c.code === form.category) ?? null
  const driver = options?.drivers.find((d) => d.id === form.driver_id) ?? null
  const lastVehicle = driver?.last_vehicle_id ? options?.vehicles.find((v) => v.id === driver.last_vehicle_id) ?? null : null
  const spread = Number(form.instalments) > 1
  const busy = preview.loading || create.loading

  const set = (k: keyof Form) => (v: string) => {
    setForm((f) => ({ ...f, [k]: v }))
    setPreviewUrl(null)
    setError(null)
  }

  // Choosing a driver brings his own cell number with it. Switching drivers
  // replaces it too, because a number left over from the last driver is exactly
  // the airtime that gets loaded onto the wrong phone.
  const chooseDriver = (id: string) => {
    const d = options?.drivers.find((x) => x.id === id)
    setForm((f) => ({ ...f, driver_id: id, phone_number: localPhone(d?.phone) }))
    setPreviewUrl(null)
    setError(null)
  }

  const body = useMemo(() => ({
    driver_id: form.driver_id || null,
    category: form.category,
    amount_zar: form.amount_zar.replace(/\s/g, '').replace(',', '.'),
    reason: form.reason,
    phone_number: category?.needs_phone ? form.phone_number : null,
    vehicle_id: form.vehicle && form.vehicle !== NO_VEHICLE ? form.vehicle : null,
    no_vehicle: form.vehicle === NO_VEHICLE,
    event_date: form.event_date || null,
    instalments: Number(form.instalments) || 1,
    terms_reason: spread ? form.terms_reason : null,
  }), [form, category, spread])

  async function showPreview() {
    setError(null)
    try {
      const r = await preview.trigger(body, { skipCache: true })
      if (r?.pdf_base64) setPreviewUrl(pdfBlobUrl(r.pdf_base64))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  async function send() {
    setError(null)
    try {
      const r = await create.trigger(body)
      if (r) {
        setSent(r)
        onSaved()
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  if (sent) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{sent.sent ? `${sent.ref} is with the driver` : `${sent.ref} is saved but not sent`}</DialogTitle>
            <DialogDescription>
              {sent.sent
                ? `SignNow has emailed ${sent.driver_email ?? 'him'} the page to sign, and you are copied. Nothing comes off his pay until he signs, and this page shows Signed as soon as he does.`
                : 'SignNow did not accept it, so the driver has nothing yet. The loan is kept as Not sent; open it in the list and use Send again.'}
            </DialogDescription>
          </DialogHeader>
          {!sent.sent && sent.send_error && (
            <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">{sent.send_error}</p>
          )}
          {sent.sent && category?.pays_out && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-100">
              Do not load the airtime or hand over the cash yet. Wait until it shows Signed, then mark it paid out.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setSent(null); setForm({ ...EMPTY }); setPreviewUrl(null) }}>Raise another</Button>
            <Button variant="brand" onClick={() => onOpenChange(false)}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o) }}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><HandCoins className="h-5 w-5" /> Raise a loan for a driver</DialogTitle>
          <DialogDescription>
            The driver gets a page to sign on SignNow. Nothing comes off his pay until he signs it, and by default the whole amount
            comes off his first payslip after he signs{options?.next_payday ? `, which today is ${options.next_payday}` : ''}.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field label="Driver" htmlFor="loan-driver" hint={driver ? `${driver.manager ? `Manager: ${driver.manager}. ` : ''}${driver.status === 'suspended' ? 'Suspended. ' : ''}${driver.has_email ? '' : 'No email on file, so SignNow cannot reach him.'}` : undefined}>
              <NativeSelect id="loan-driver" value={form.driver_id} onChange={(e) => chooseDriver(e.target.value)}>
                <option value="">Choose the driver</option>
                {(options?.drivers ?? []).map((d) => (
                  <option key={d.id} value={d.id}>{d.name}{d.status === 'suspended' ? ' (suspended)' : ''}</option>
                ))}
              </NativeSelect>
            </Field>
          </div>

          <div className="sm:col-span-2 space-y-1">
            <span className="text-xs font-medium text-foreground">What is it for</span>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" role="radiogroup" aria-label="What is it for">
              {(options?.categories ?? []).map((c) => (
                <button
                  key={c.code}
                  type="button"
                  role="radio"
                  aria-label={c.label}
                  aria-checked={form.category === c.code}
                  onClick={() => set('category')(c.code)}
                  className={cn(
                    'rounded-md border px-3 py-2 text-left text-xs transition-colors',
                    form.category === c.code
                      ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                      : 'border-border bg-background text-foreground hover:bg-accent',
                  )}
                >
                  <span className="block font-medium">{c.label}</span>
                </button>
              ))}
            </div>
            {category && <p className="text-[11px] text-muted-foreground">{category.description}</p>}
          </div>

          <Field label="Amount (rand)" htmlFor="loan-amount">
            <Input id="loan-amount" inputMode="decimal" placeholder="e.g. 150" value={form.amount_zar} onChange={(e) => set('amount_zar')(e.target.value)} />
          </Field>

          {category?.needs_phone ? (
            <Field label="Cell number for the airtime" htmlFor="loan-phone" hint="The number the airtime or data is loaded onto. It is printed on the page he signs.">
              <Input id="loan-phone" inputMode="tel" placeholder="0821234567" value={form.phone_number} onChange={(e) => set('phone_number')(e.target.value)} />
            </Field>
          ) : (
            <Field label="Date it happened (optional)" htmlFor="loan-date">
              <Input id="loan-date" type="date" value={form.event_date} onChange={(e) => set('event_date')(e.target.value)} />
            </Field>
          )}

          <div className="sm:col-span-2">
            <Field label="What it is for, in words the driver will recognise" htmlFor="loan-reason">
              <Textarea id="loan-reason" rows={2} value={form.reason} onChange={(e) => set('reason')(e.target.value)}
                placeholder={form.category === 'repair' ? 'e.g. Rear bumper after reversing into a pole' : form.category === 'uber_cash' ? 'e.g. Cash trips 14 to 20 September not deposited' : 'e.g. Airtime to call passengers'} />
            </Field>
          </div>

          <div className="sm:col-span-2">
            <Field label="Vehicle" htmlFor="loan-vehicle"
              hint={lastVehicle && form.vehicle !== driver?.last_vehicle_id
                ? <button type="button" className="underline underline-offset-2" onClick={() => set('vehicle')(driver!.last_vehicle_id!)}>He last drove {lastVehicle.label}. Use it</button>
                : category?.vehicle_rule === 'required' ? 'A repair is always to a vehicle.' : undefined}>
              <NativeSelect id="loan-vehicle" value={form.vehicle} onChange={(e) => set('vehicle')(e.target.value)}>
                <option value="">Choose the vehicle, or say none was involved</option>
                {category?.vehicle_rule !== 'required' && <option value={NO_VEHICLE}>No vehicle involved</option>}
                {(options?.vehicles ?? []).map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
              </NativeSelect>
            </Field>
          </div>

          <div className="sm:col-span-2 space-y-2 rounded-md border border-border p-3">
            <span className="text-xs font-medium text-foreground">Payment terms</span>
            <label className="flex items-start gap-2 text-sm">
              <input type="radio" name="terms" className="mt-1" checked={!spread} onChange={() => set('instalments')('1')} />
              <span>The whole amount from his first payslip after he signs{options?.next_payday ? ` (${options.next_payday} if he signs in time)` : ''}</span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input type="radio" name="terms" className="mt-1" checked={spread} onChange={() => set('instalments')('3')} />
              <span className="flex flex-wrap items-center gap-2">
                Spread over
                <NativeSelect className="h-8 w-20 text-xs" value={spread ? form.instalments : '3'} disabled={!spread} onChange={(e) => set('instalments')(e.target.value)} aria-label="Number of months">
                  {Array.from({ length: 11 }, (_, i) => i + 2).map((n) => <option key={n} value={n}>{n}</option>)}
                </NativeSelect>
                monthly payslips, at my discretion
              </span>
            </label>
            {spread && (
              <Field label="Why it is spread (printed on the page he signs)" htmlFor="loan-terms">
                <Input id="loan-terms" value={form.terms_reason} onChange={(e) => set('terms_reason')(e.target.value)} placeholder="e.g. Large repair, spread so his pay is not wiped out" />
              </Field>
            )}
            <p className="text-[11px] text-muted-foreground">Preview shows each payslip and amount exactly as he will see them.</p>
          </div>
        </div>

        {previewUrl && (
          <div className="space-y-2">
            <iframe title="The page the driver signs" src={previewUrl} className="hidden h-[28rem] w-full rounded-md border border-border sm:block" />
            <a href={previewUrl} target="_blank" rel="noreferrer" className="text-xs underline underline-offset-2">Open the page in a new tab</a>
          </div>
        )}

        {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p>}

        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button variant="outline" onClick={() => void showPreview()} disabled={busy}>
            {preview.loading ? <Spinner className="mr-1 h-4 w-4" /> : <Eye className="mr-1 h-4 w-4" />} Preview the page
          </Button>
          <Button variant="brand" onClick={() => void send()} disabled={busy}>
            {create.loading ? <Spinner className="mr-1 h-4 w-4" /> : <Send className="mr-1 h-4 w-4" />} Send to the driver to sign
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
